import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Upload, FileText, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result); // data:...;base64,....
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function Add() {
  const inputRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const pick = () => inputRef.current?.click();

  const onFiles = (files) => {
    const arr = Array.from(files || []);
    setQueue((q) => [...q, ...arr.map((f) => ({ file: f, status: "pending" }))]);
  };

  const upload = async () => {
    if (queue.length === 0 || busy) return;
    setBusy(true);
    let uploadedIds = [];
    for (let i = 0; i < queue.length; i++) {
      const item = queue[i];
      if (item.status === "done") continue;
      try {
        setQueue((q) => q.map((x, idx) => (idx === i ? { ...x, status: "uploading" } : x)));
        const base64 = await fileToBase64(item.file);
        const { data } = await api.post("/documents/upload", {
          filename: item.file.name,
          mime_type: item.file.type || "application/octet-stream",
          base64_data: base64,
          title: title || item.file.name,
          note: note || null,
        });
        uploadedIds.push(data?.document_id || data?.id);
        setQueue((q) => q.map((x, idx) => (idx === i ? { ...x, status: "done" } : x)));
      } catch (err) {
        setQueue((q) => q.map((x, idx) => (idx === i ? { ...x, status: "error" } : x)));
        toast.error(`Failed to upload ${item.file.name}`);
      }
    }
    setBusy(false);
    toast.success("Uploaded. Memora is extracting details in the background.");
    setTimeout(() => {
      setQueue([]);
      setTitle("");
      setNote("");
      navigate("/records/documents");
    }, 600);
  };

  return (
    <div className="space-y-6" data-testid="add-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Add to your Memora</h1>
        <p className="text-neutral-600 mt-1 text-sm">Upload lab reports, prescriptions, imaging, or any health document. Memora will read and file it.</p>
      </div>

      <div
        onClick={pick}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-neutral-300 rounded-2xl p-10 text-center bg-white hover:border-neutral-400 cursor-pointer"
        data-testid="dropzone"
      >
        <Upload className="w-8 h-8 mx-auto text-neutral-400" />
        <div className="mt-3 font-medium">Click or drop files here</div>
        <div className="text-sm text-neutral-500 mt-1">PDF, images (JPG/PNG). Add as many as you want.</div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/*,application/pdf"
          className="hidden"
          onChange={(e) => onFiles(e.target.files)}
          data-testid="file-input"
        />
      </div>

      {queue.length > 0 && (
        <div className="space-y-3">
          <div className="grid gap-3">
            <div>
              <label className="text-sm text-neutral-700 mb-1 block">Title (optional)</label>
              <Input data-testid="add-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Annual physical, March 2026" />
            </div>
            <div>
              <label className="text-sm text-neutral-700 mb-1 block">Note (optional)</label>
              <Textarea data-testid="add-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Anything Memora should know?" rows={2} />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-neutral-200 divide-y divide-neutral-100">
            {queue.map((q, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <FileText className="w-4 h-4 text-neutral-400" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{q.file.name}</div>
                  <div className="text-xs text-neutral-500">{Math.round(q.file.size / 1024)} KB</div>
                </div>
                {q.status === "uploading" && <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />}
                {q.status === "done" && <Check className="w-4 h-4 text-emerald-600" />}
                {q.status === "error" && <span className="text-xs text-rose-600">error</span>}
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setQueue([])} disabled={busy}>Clear</Button>
            <Button data-testid="add-upload-btn" onClick={upload} disabled={busy}>
              {busy ? "Uploading…" : `Upload ${queue.length} file${queue.length > 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
